import React, { useEffect, useState, useCallback } from "react";
import { useLocation } from 'react-router-dom';
import "../light-theme.css";
import API from "../services/api";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Settings from "../components/Settings";
import CalendarView from "../components/CalendarView";
import Sidebar from "../components/Sidebar";
import DashboardOverview from "../components/DashboardOverview";
import ProfileCard from "../components/ProfileCard";
import TasksView from "../components/TasksView";
import Modals from "../components/Models";
import DashboardFooter from "../components/DashboardFooter";
import PhotoEditor from "../components/PhotoEditor";
import "../App.css";
import "../Dashboard.css";


const Dashboard = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth > 768);
  const [graphFilter, setGraphFilter] = useState('Real-time');
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSupportOpen, setIsSupportOpen] = useState(false);
  const [isPrivacyOpen, setIsPrivacyOpen] = useState(false);
  const [isLogoutConfirmOpen, setIsLogoutConfirmOpen] = useState(false);
  const [isAiLoading, setIsAiLoading] = useState(false);
  const [isSavingTask, setIsSavingTask] = useState(false);
  const [isEditingSaving, setIsEditingSaving] = useState(false);
  const [deletingTaskId, setDeletingTaskId] = useState(null);
  const [updatingTaskId, setUpdatingTaskId] = useState(null);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [filter, setFilter] = useState("pending");
  const [pendingTasks, setPendingTasks] = useState([]);
  const [missedTasks, setMissedTasks] = useState([]);
  const [completedTasks, setCompletedTasks] = useState([]);
  const [newTask, setNewTask] = useState({ title: "", description: "", due_date: "" });
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFilter, setSelectedFilter] = useState('none');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const resetNewTaskForm = () => {
    setNewTask({ title: "", description: "", due_date: "" });
  };
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showPhotoMenu, setShowPhotoMenu] = useState(false);
  const [showPhotoEditor, setShowPhotoEditor] = useState(false);
  const [photoToEdit, setPhotoToEdit] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeletingPhoto, setIsDeletingPhoto] = useState(false);
  const [showTaskDeleteConfirm, setShowTaskDeleteConfirm] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const token = localStorage.getItem("token");

  const themeAttr = typeof document !== "undefined"
    ? document.documentElement.getAttribute("data-theme")
    : "dark";
  const isLightTheme = themeAttr === "light";
  const navyTextPrimary = isLightTheme ? "#0b1f3f" : "#f4f8ff";
  const navyMutedText = isLightTheme ? "rgba(11, 31, 63, 0.6)" : "rgba(214, 228, 255, 0.65)";
  const profileSurfaceStyle = {
    background: isLightTheme
      ? "linear-gradient(135deg, #eef4ff, #dbe6ff)"
      : "linear-gradient(135deg, rgba(4, 16, 38, 0.95), rgba(9, 27, 58, 0.88))",
    border: isLightTheme
      ? "1px solid rgba(9, 28, 66, 0.1)"
      : "1px solid rgba(42, 126, 255, 0.22)",
    boxShadow: isLightTheme
      ? "0 18px 32px rgba(9, 28, 66, 0.15)"
      : "0 24px 52px rgba(1, 6, 18, 0.75)",
    color: navyTextPrimary,
    borderRadius: "20px",
    backdropFilter: "blur(12px)",
    WebkitBackdropFilter: "blur(12px)"
  };
  const profileInfoCardStyle = {
    padding: "12px",
    borderRadius: "14px",
    background: isLightTheme
      ? "linear-gradient(135deg, rgba(19, 55, 109, 0.08), rgba(255, 255, 255, 0.92))"
      : "linear-gradient(135deg, rgba(8, 60, 124, 0.4), rgba(2, 8, 22, 0.72))",
    border: isLightTheme
      ? "1px solid rgba(16, 43, 92, 0.12)"
      : "1px solid rgba(59, 143, 255, 0.28)",
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    transition: "all 0.3s ease",
    cursor: "pointer"
  };
  const infoLabelColor = isLightTheme ? "#1c4fa3" : "#7cd3ff";
  const profileBadgeLockedBg = isLightTheme ? "rgba(10, 30, 60, 0.06)" : "rgba(0, 5, 15, 0.65)";
  const profileBadgeShadow = isLightTheme
    ? "0 16px 30px rgba(9, 23, 52, 0.15)"
    : "0 20px 38px rgba(0, 5, 15, 0.65)";
  const profileProgressTrack = isLightTheme ? "rgba(11, 31, 63, 0.12)" : "rgba(124, 211, 255, 0.2)";
  const profileAccountCardBg = isLightTheme ? "rgba(9, 31, 64, 0.05)" : "rgba(0, 6, 18, 0.65)";
  const profileAccountBorder = isLightTheme
    ? "1px solid rgba(11, 31, 63, 0.08)"
    : "1px solid rgba(63, 142, 255, 0.2)";
  
  // Get profile image from backend, not localStorage
  const [profileImage, setProfileImage] = useState("");
  const [userInfo, setUserInfo] = useState({ username: "", email: "", created_at: null });

  const timeString = currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
  const dateString = currentTime.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' });

  const fetchTasks = useCallback(async (selFilter = 'none') => {
    try {
      const params = {};
      if (selFilter && selFilter !== 'none') params.filter = selFilter;
      const res = await API.get("/tasks/", { params, headers: { Authorization: `Bearer ${token}` } });
      // compare date-only (YYYY-MM-DD) in local timezone to avoid timezone time-component issues
      const toLocalYMD = (d) => {
        if (!d) return null;
        const dt = new Date(d);
        const y = dt.getFullYear();
        const m = String(dt.getMonth() + 1).padStart(2, '0');
        const day = String(dt.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
      };
      const todayYMD = toLocalYMD(new Date());
      // Split into groups while preserving server ordering, then apply
      // explicit per-group sorting for a professional feel.
      const pending = res.data.filter(t => {
        if (t.is_completed) return false;
        if (!t.due_date) return true;
        const due = toLocalYMD(t.due_date);
        return due >= todayYMD;
      });

      const missed = res.data.filter(t => {
        if (t.is_completed) return false;
        if (!t.due_date) return false;
        const due = toLocalYMD(t.due_date);
        return due < todayYMD;
      });

      const completed = res.data.filter(t => t.is_completed);

      // Sorting helpers
      const nameCmp = (a, b) => {
        const at = (a.title || '').toLowerCase();
        const bt = (b.title || '').toLowerCase();
        return at.localeCompare(bt);
      };

      const dateCmp = (a, b) => {
        // tasks with due_date come first
        if (!a.due_date && !b.due_date) return 0;
        if (!a.due_date) return 1;
        if (!b.due_date) return -1;
        return new Date(a.due_date) - new Date(b.due_date);
      };

      if (selFilter === 'name') {
        pending.sort(nameCmp);
        missed.sort(nameCmp);
        completed.sort(nameCmp);
      } else if (selFilter === 'date') {
        pending.sort(dateCmp);
        missed.sort(dateCmp);
        completed.sort(dateCmp);
      }

      setPendingTasks(pending);
      setMissedTasks(missed);
      setCompletedTasks(completed);
    } catch (err) { 
      toast.error("Session expire login Again"); 
    }
  }, [token]);

  const applyFilter = async (value) => {
    const sel = value || selectedFilter;
    // ensure state stays in sync when called programmatically
    setSelectedFilter(sel);
    await fetchTasks(sel);
  };

  const fetchProfileImage = useCallback(async () => {
    try {
      const res = await API.get("/auth/profile/image", { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.profile_image) {
        setProfileImage(res.data.profile_image);
      }
    } catch (err) {
      console.error("Failed to fetch profile image");
    }
  }, [token]);

  const fetchUserInfo = useCallback(async () => {
    try {
      const res = await API.get("/auth/user/info", { headers: { Authorization: `Bearer ${token}` } });
      setUserInfo({
        username: res.data.username || "",
        email: res.data.email || "",
        created_at: res.data.created_at || null
      });
    } catch (err) {
      console.error("Failed to fetch user info");
    }
  }, [token]);

  useEffect(() => {
    if (!token) return navigate("/login");
    fetchTasks();
    fetchProfileImage();
    fetchUserInfo();
  }, [token, navigate, fetchTasks, fetchProfileImage, fetchUserInfo]);

  const handleProfileImageChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      const dataUrl = reader.result;
      if (typeof dataUrl === "string") {
        setPhotoToEdit(dataUrl);
        setShowPhotoEditor(true);
        setShowPhotoMenu(false);
      }
    };
    reader.readAsDataURL(file);
    // Reset input value so same file can be selected again
    e.target.value = '';
  };

  const handlePhotoEditorSave = async (editedImageData) => {
    try {
      await API.put("/auth/profile/image", 
        { profile_image: editedImageData },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProfileImage(editedImageData);
      setShowPhotoEditor(false);
      setPhotoToEdit(null);
      toast.success("Profile photo updated successfully");
    } catch (err) {
      toast.error("Failed to update profile photo");
    }
  };

  const handlePhotoEditorCancel = () => {
    setShowPhotoEditor(false);
    setPhotoToEdit(null);
  };

  const handleProfileImageDelete = async () => {
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    setIsDeletingPhoto(true);
    try {
      await API.delete("/auth/profile/image", { headers: { Authorization: `Bearer ${token}` } });
      setProfileImage("");
      setShowPhotoMenu(false);
      setShowDeleteConfirm(false);
      toast.success("Profile photo deleted successfully");
    } catch (err) {
      toast.error("Failed to delete profile photo");
      setShowDeleteConfirm(false);
    } finally {
      setIsDeletingPhoto(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  // Update tab title based on active section
  useEffect(() => {
    const titles = {
      dashboard: "Cortex - Dashboard",
      tasks: "Cortex - Tasks",
      settings: "Cortex - Settings",
      profile: "Cortex - Profile",
      calendar: "Cortex - Calendar"
    };
    document.title = titles[activeTab] || "Cortex";
  }, [activeTab]);

  // Sync activeTab from URL on load / when URL changes
  useEffect(() => {
    const parts = location.pathname.split('/').filter(Boolean); // e.g. ['dashboard','settings'] or ['settings']
    let tab = 'dashboard';
    if (parts.length === 0) {
      tab = 'dashboard';
    } else if (parts[0] === 'dashboard') {
      tab = parts[1] || 'dashboard';
    } else if (['settings', 'tasks', 'profile', 'dashboard', 'calendar'].includes(parts[0])) {
      tab = parts[0];
    }
    if (tab && ['dashboard', 'tasks', 'settings', 'profile', 'calendar'].includes(tab)) {
      setActiveTab(tab);
    }
  }, [location.pathname]);

  // Apply task filter when coming from calendar (sessionStorage driven so it survives navigation)
  useEffect(() => {
    if (activeTab !== 'tasks') return;
    let taskFilter = null;
    try {
      taskFilter = sessionStorage.getItem('tasksFilterFromCalendar');
    } catch (e) {}

    if (taskFilter && ['pending', 'missed', 'completed'].includes(taskFilter)) {
      setFilter(taskFilter);
      setSelectedFilter('none');
      setSearchQuery('');
      try { sessionStorage.removeItem('tasksFilterFromCalendar'); } catch (e) {}
    }
  }, [activeTab]);

  // wrapper to change tab and update URL
  const handleSetActiveTab = (tab, navOptions = {}) => {
    setActiveTab(tab);
    const navState = navOptions.state || {};
    // push new URL so reload keeps this tab
    if (tab === 'dashboard') navigate('/dashboard', { replace: false, state: navState });
    else navigate(`/${tab}`, { replace: false, state: navState });
  };

  const handleCalendarNavigate = (status) => {
    // Map status to filter tabs
    let filterKey = 'pending';
    if (status === 'Overdue') filterKey = 'missed';
    else if (status === 'Completed') filterKey = 'completed';

    setFilter(filterKey);
    try { sessionStorage.setItem('tasksFilterFromCalendar', filterKey); } catch (e) {}
    handleSetActiveTab('tasks');
  };

  const handleAIMagic = async (e) => {
    if (e) e.preventDefault();
    const titleInput = document.querySelector('input[placeholder="Enter task title"]');
    if (!newTask.title || newTask.title.trim() === "") {
      if (titleInput) {
        titleInput.setCustomValidity("Please enter a title before using AI assistance");
        titleInput.reportValidity();
        titleInput.setCustomValidity("");
      }
      return;
    }
    setIsAiLoading(true);
    try {
      const res = await API.post("/tasks/ai/breakdown", { task_description: newTask.title }, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data && res.data.steps) {
        const description = res.data.steps.join('\n');
        setNewTask({ ...newTask, description: description });
        toast.success("Task description generated ");
      }
    } catch (err) {
      toast.error("The AI service is currently unavailable");
    } finally {
      setIsAiLoading(false);
    }
  };

  useEffect(() => {
    const intervalId = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsSidebarOpen(!mobile);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleAddTask = async (e) => {
  e.preventDefault();

  // 1. Check karein agar date di gayi hai (Optional check)
  if (newTask.due_date) {
    const today = new Date();
    today.setHours(0, 0, 0, 0); 

    const selectedDate = new Date(newTask.due_date);

    // 2. Sirf tab check karein jab date valid ho
    if (selectedDate < today) {
      toast.error("Past dates are not allowed!");
      return; // Yahin ruk jao
    }
  }

  // 3. Ab asli save karne wala code shuru hoga
  setIsSavingTask(true);
  try {
    const token = localStorage.getItem("token");
    
    // Validate title is not empty
    if (!newTask.title || newTask.title.trim() === "") {
      toast.error("Title is required!");
      setIsSavingTask(false); 
      return;
    }

    // Prepare task payload - IMPORTANT: title must not be empty!
    const taskPayload = {
      title: newTask.title.trim(),
      description: newTask.description && newTask.description.trim() ? newTask.description.trim() : null,
      due_date: newTask.due_date || null
    };
    
    console.log("Sending payload:", taskPayload);
    
    // Check payload size before sending (prevent 413 errors)
    const payloadSize = new Blob([JSON.stringify(taskPayload)]).size;
    const maxSize = 1048576 * 10; // 10MB limit
    
    if (payloadSize > maxSize) {
      toast.error(`Task too large (${(payloadSize / 1048576).toFixed(2)}MB). Maximum is 10MB.`);
      setIsSavingTask(false);
      return;
    }

    const res = await API.post("/tasks", taskPayload, {
      headers: { Authorization: `Bearer ${token}` }
    });

    if (res.status === 201 || res.status === 200) {
      toast.success("Task successfully added");
      
      // Local list update karne ka logic (jo aapka pehle tha)
      setPendingTasks([...pendingTasks, res.data]); 
      
      // Modal band karein aur form reset karein
      setIsModalOpen(false);
      setNewTask({ title: "", description: "", due_date: "" });
    }
  } catch (err) {
    console.error("Save Error:", err);
    console.error("Response data:", err.response?.data);
    
    // Show actual error from backend
    let errorMessage = "Failed to save task";
    if (err.response?.data?.detail) {
      // If it's an array of validation errors, extract the message
      if (Array.isArray(err.response.data.detail)) {
        errorMessage = err.response.data.detail.map(e => e.msg).join(", ");
      } else {
        errorMessage = err.response.data.detail;
      }
    } else if (err.response?.status === 422) {
      errorMessage = "Invalid task data - Title and Description required";
    } else if (err.message) {
      errorMessage = err.message;
    }
    
    toast.error(errorMessage);
  } finally {
    setIsSavingTask(false);
  }
};


  const handleLogout = () => {
  try {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    // clear any agent session keys (legacy or per-user)
    try { Object.keys(localStorage).forEach(k => { if (typeof k === 'string' && (k.startsWith('agent_sessions') || k.startsWith('agent_active_session'))) localStorage.removeItem(k); }); } catch (e) {}
  } catch (e) {}
  // Keep theme preference - don't reset it
  navigate("/login", { replace: true });
};


  const handleUpdateStatus = async (id, isCompleted) => {
    setUpdatingTaskId(id);
    try {
      await API.put(`/tasks/${id}/`, { is_completed: isCompleted }, { headers: { Authorization: `Bearer ${token}` } });

      // Optimistic UI move before showing toast
      const findTask = () => {
        return pendingTasks.find(t => t.id === id)
          || missedTasks.find(t => t.id === id)
          || completedTasks.find(t => t.id === id);
      };

      const task = findTask();
      if (task) {
        const updatedTask = { ...task, is_completed: isCompleted };
        if (isCompleted) {
          // Move to completed
          setCompletedTasks(prev => [...prev.filter(t => t.id !== id), updatedTask]);
          setPendingTasks(prev => prev.filter(t => t.id !== id));
          setMissedTasks(prev => prev.filter(t => t.id !== id));
          toast.success("Task marked as completed");
        } else {
          // Move back to pending or missed based on due date
          // Use date-only comparison to decide where to restore the task
          const toLocalYMD = (d) => {
            if (!d) return null;
            const dt = new Date(d);
            const y = dt.getFullYear();
            const m = String(dt.getMonth() + 1).padStart(2, '0');
            const day = String(dt.getDate()).padStart(2, '0');
            return `${y}-${m}-${day}`;
          };
          const todayYMD = toLocalYMD(new Date());
          const dueYMD = updatedTask.due_date ? toLocalYMD(updatedTask.due_date) : null;
          const goesToMissed = dueYMD && dueYMD < todayYMD;

          setCompletedTasks(prev => prev.filter(t => t.id !== id));
          if (goesToMissed) {
            setMissedTasks(prev => [...prev.filter(t => t.id !== id), updatedTask]);
            setPendingTasks(prev => prev.filter(t => t.id !== id));
          } else {
            setPendingTasks(prev => [...prev.filter(t => t.id !== id), updatedTask]);
            setMissedTasks(prev => prev.filter(t => t.id !== id));
          }
          toast.info("Task restored");
        }
      } else {
        // Fallback toast if task not found locally
        if (isCompleted) toast.success("Task marked as completed");
        else toast.info("Task restored");
      }

      // Re-sync from server to avoid drift
      await fetchTasks();
    } catch (err) { 
      toast.error("Status update failed"); 
    } finally {
      setUpdatingTaskId(null);
    }
  };

  const handleDelete = (id) => {
    const task = pendingTasks.find(t => t.id === id)
      || missedTasks.find(t => t.id === id)
      || completedTasks.find(t => t.id === id);
    setTaskToDelete(task || { id, title: "this task" });
    setShowTaskDeleteConfirm(true);
  };

  const confirmTaskDelete = async () => {
    if (!taskToDelete?.id) return;
    setDeletingTaskId(taskToDelete.id);
    try {
      await API.delete(`/tasks/${taskToDelete.id}`, { headers: { Authorization: `Bearer ${token}` } });
      await fetchTasks();
      toast.success("Task Deleted");
    } catch (err) {
      toast.error("Failed to delete the selected task");
    } finally {
      setDeletingTaskId(null);
      setShowTaskDeleteConfirm(false);
      setTaskToDelete(null);
    }
  };

  const cancelTaskDelete = () => {
    setShowTaskDeleteConfirm(false);
    setTaskToDelete(null);
  };

  const handleSaveEdit = async () => {
  setIsEditingSaving(true);
  try {
    const token = localStorage.getItem("token");
    
    // Backend API Call (Adjust endpoint as per your FastAPI routes)
    const res = await API.put(`/tasks/${selectedTask.id}`, {
      title: selectedTask.title,
      description: selectedTask.description,
      due_date: selectedTask.due_date || null
    }, {
      headers: { Authorization: `Bearer ${token}` }
    });

    if (res.status === 200) {
      toast.success("Task updated successfully");
      
      // Local state ko update karein taake page refresh na karna paray
      const updateList = (list) => list.map(t => t.id === selectedTask.id ? selectedTask : t);
      
      if (filter === 'pending') setPendingTasks(updateList(pendingTasks));
      else if (filter === 'missed') setMissedTasks(updateList(missedTasks));
      else setCompletedTasks(updateList(completedTasks));

      setIsViewModalOpen(false); // Modal band kar dein
      setIsEditMode(false);
    }
  } catch (err) {
    console.error(err);
    toast.error("Failed to sync updates with server");
  } finally {
    setIsEditingSaving(false);
  }
};


const getFilteredData = () => {
  const total = pendingTasks.length + missedTasks.length + completedTasks.length;
  
  if (graphFilter === 'Real-time') {
    return [
      { name: 'PENDING', count: pendingTasks.length },
      { name: 'OVERDUE', count: missedTasks.length },
      { name: 'COMPLETED', count: completedTasks.length },
      { name: 'TOTAL', count: total }
    ];
  }
  
  // Weekly/Monthly ke liye hum thora farzi (simulated) data dikha sakte hain 
  // jab tak aapka backend dates support na kare
  if (graphFilter === 'Weekly') {
    return [
      { name: 'Mon', count: 2 }, { name: 'Tue', count: 5 },
      { name: 'Wed', count: pendingTasks.length }, { name: 'Thu', count: 4 },
      { name: 'Fri', count: completedTasks.length }, { name: 'Sat', count: 8 },
      { name: 'Sun', count: 3 }
    ];
  }

  if (graphFilter === 'Monthly') {
    return [
      { name: 'Week 1', count: 10 }, { name: 'Week 2', count: 15 },
      { name: 'Week 3', count: total }, { name: 'Week 4', count: 12 }
    ];
  }

  return dynamicGraphData; // Default
};


const dynamicGraphData = [
  { name: 'PENDING', count: pendingTasks.length },
  { name: 'OVERDUE', count: missedTasks.length },
  { name: 'COMPLETED', count: completedTasks.length },
  { name: 'TOTAL', count: pendingTasks.length + missedTasks.length + completedTasks.length }
];

  const totalTasks = pendingTasks.length + missedTasks.length + completedTasks.length;
  const completionRate = totalTasks ? Math.min(Math.round((completedTasks.length / totalTasks) * 100), 100) : 0;

  const nextDueTask = [...pendingTasks]
    .filter(t => !!t.due_date)
    .sort((a, b) => new Date(a.due_date) - new Date(b.due_date))[0] || null;

  const oldestOverdue = [...missedTasks]
    .filter(t => !!t.due_date)
    .sort((a, b) => new Date(a.due_date) - new Date(b.due_date))[0] || null;
  const profileProgressPercent = Math.min(Math.floor((completedTasks.length / 100) * 100), 100);
  const isBeginnerUnlocked = completedTasks.length >= 1;
  const isMasterUnlocked = completedTasks.length >= 10;
  const isProductiveUnlocked = completedTasks.length >= 50;
  const isLegendUnlocked = completedTasks.length >= 100;

  return (
    <div className={`dashboard-container ${activeTab === "settings" ? "is-settings-active" : ""} ${!isSidebarOpen ? "sidebar-closed" : ""}`}>
      {showPhotoEditor && photoToEdit && (
        <PhotoEditor 
          imageSrc={photoToEdit}
          onSave={handlePhotoEditorSave}
          onCancel={handlePhotoEditorCancel}
        />
      )}
      
      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={cancelDelete}>
          <div
            className="modal-content ultra-glass danger-mode"
            style={{
              maxWidth: '420px',
              width: '90%',
              textAlign: 'center',
              padding: '36px'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button className="close-btn" onClick={cancelDelete}>✕</button>

            <h3 className="modal-title" style={{ marginBottom: '14px' }}>Delete Profile Photo?</h3>

            <p style={{
              fontSize: '14px',
              lineHeight: '1.6',
              marginBottom: '28px'
            }}>
              Are you sure you want to delete your profile photo?
            </p>

            <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
              <button
                className="save-btn cancel-btn"
                onClick={cancelDelete}
                style={{
                  background: document.documentElement.getAttribute('data-theme') === 'light'
                    ? '#ffffff'
                    : 'rgba(255,255,255,0.05)',
                  color: document.documentElement.getAttribute('data-theme') === 'light'
                    ? '#202124'
                    : '#ffffff',
                  flex: 1,
                  cursor: 'pointer',
                  marginTop: 0
                }}
              >
                CANCEL
              </button>

              <button
                className="save-btn"
                onClick={confirmDelete}
                disabled={isDeletingPhoto}
                style={{
                  background: isDeletingPhoto
                    ? (document.documentElement.getAttribute('data-theme') === 'light' ? 'rgba(255, 123, 123, 0.7)' : 'rgba(255, 107, 107, 0.7)')
                    : (document.documentElement.getAttribute('data-theme') === 'light' ? '#ff7b7b' : '#ff6b6b'),
                  color: '#fff',
                  border: 'none',
                  flex: 1,
                  cursor: isDeletingPhoto ? 'not-allowed' : 'pointer',
                  opacity: isDeletingPhoto ? 0.85 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  minHeight: '45px',
                  marginTop: 0
                }}
              >
                {isDeletingPhoto ? (
                  <div style={{
                    width: '18px',
                    height: '18px',
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                    borderTop: '2px solid #fff',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                ) : 'DELETE'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showTaskDeleteConfirm && (
        <div className="modal-overlay" onClick={cancelTaskDelete}>
          <div
            className="modal-content ultra-glass danger-mode"
            style={{
              maxWidth: '440px',
              width: '90%',
              textAlign: 'center',
              padding: '36px'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button className="close-btn" onClick={cancelTaskDelete}>✕</button>

            <h3 className="modal-title" style={{ marginBottom: '14px' }}>Delete Task?</h3>

            <p style={{
              fontSize: '14px',
              lineHeight: '1.6',
              marginBottom: '28px'
            }}>
              Are you sure you want to delete “{taskToDelete?.title || 'this task'}”? This action cannot be undone.
            </p>

            <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
              <button
                className="save-btn cancel-btn"
                onClick={cancelTaskDelete}
                style={{
                  background: document.documentElement.getAttribute('data-theme') === 'light'
                    ? '#ffffff'
                    : 'rgba(255,255,255,0.05)',
                  color: document.documentElement.getAttribute('data-theme') === 'light'
                    ? '#202124'
                    : '#ffffff',
                  flex: 1,
                  cursor: 'pointer',
                  marginTop: 0
                }}
              >
                CANCEL
              </button>

              <button
                className="save-btn"
                onClick={confirmTaskDelete}
                disabled={deletingTaskId === taskToDelete?.id}
                style={{
                  background: deletingTaskId === taskToDelete?.id
                    ? (document.documentElement.getAttribute('data-theme') === 'light' ? 'rgba(223, 100, 100, 0.7)' : 'rgba(255, 107, 107, 0.7)')
                    : (document.documentElement.getAttribute('data-theme') === 'light' ? '#ff7b7b' : '#ce7070'),
                  color: '#fff',
                  border: 'none',
                  flex: 1,
                  cursor: deletingTaskId === taskToDelete?.id ? 'not-allowed' : 'pointer',
                  opacity: deletingTaskId === taskToDelete?.id ? 0.85 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  minHeight: '45px',
                  marginTop: 0
                }}
              >
                {deletingTaskId === taskToDelete?.id ? (
                  <div style={{
                    width: '18px',
                    height: '18px',
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                    borderTop: '2px solid #fff',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                ) : 'DELETE'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Sidebar Component */}
      <Sidebar
        isSidebarOpen={isSidebarOpen}
        setIsSidebarOpen={setIsSidebarOpen}
        activeTab={activeTab}
        setActiveTab={handleSetActiveTab}
        setIsLogoutConfirmOpen={setIsLogoutConfirmOpen}
        userEmail={userInfo.email}
        userName={userInfo.username}
      />

      {/* Main Content */}
      <main className="main-content" style={{
        flexGrow: 1,
        transition: "all 0.3s ease",
        width: "100%",
      }}>
        {activeTab !== "profile" && (
          <ProfileCard 
          onClick={() => handleSetActiveTab("profile")} 
            profileImage={profileImage}
          />
        )}

        <div className="content-wrapper">
          {activeTab === "profile" ? (
            <>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
              width: '110%',
              maxWidth: '100%'
            }}>
              {/* Profile Info Header - Top Left */}
              <div
                style={{
                  ...profileSurfaceStyle,
                  width: '100%',
                  padding: '20px'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '24px' }}>👤</span>
                  <div>
                    <h3 style={{ 
                      marginBottom: '4px',
                      fontSize: '20px',
                      fontWeight: '700',
                      background: 'linear-gradient(135deg, #0b5ad9, #3ec7ff)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      margin: '0px'
                    }}>Profile Information</h3>
                    <p style={{ 
                      color: navyMutedText,
                      fontSize: '13px',
                      margin: '0px'
                    }}> Manage your account details</p>
                  </div>
                </div>
              </div>

              {/* Profile Photo - Centered */}
              <div style={{
                ...profileSurfaceStyle,
                display: 'flex',
                justifyContent: 'center',
                padding: '28px',
                position: 'relative',
                zIndex: 5,
                overflow: 'visible'
              }}>
                <div style={{
                  width: '108px',
                  height: '108px',
                  borderRadius: '50%',
                  background: isLightTheme
                    ? '#ffffff'
                    : 'linear-gradient(135deg, #051d3d, #0c396d)',
                  overflow: 'visible',
                  border: isLightTheme
                    ? '2px solid rgba(9, 31, 64, 0.08)'
                    : '2px solid rgba(88, 168, 255, 0.35)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                  marginBottom: '18px',
                  boxShadow: isLightTheme
                    ? '0 18px 34px rgba(12, 28, 56, 0.18)'
                    : '0 18px 38px rgba(0, 5, 18, 0.7)'
                }}>
                  {profileImage ? (
                    <img src={profileImage} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                  ) : (
                    <svg
                      width="60"
                      height="60"
                      viewBox="0 0 64 64"
                      aria-hidden="true"
                      style={{ display: 'block' }}
                    >
                      <circle cx="32" cy="32" r="32" fill="#f2f2f2" />
                      <circle cx="32" cy="26" r="12" fill="#c9c9c9" />
                      <path
                        d="M12 56c4-12 16-18 20-18s16 6 20 18"
                        fill="#c9c9c9"
                      />
                    </svg>
                  )}

                  <label
                    style={{
                      position: 'absolute',
                      right: '-6px',
                      bottom: '4px',
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #0c1f3d, #16509c)',
                      border: '1px solid rgba(124, 211, 255, 0.4)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      fontSize: '14px',
                      color: '#fff',
                      boxShadow: '0 10px 16px rgba(0, 5, 15, 0.45)',
                      transition: 'all 0.3s ease'
                    }}
                    title="Change photo"
                    onClick={() => setShowPhotoMenu(!showPhotoMenu)}
                    onMouseEnter={(e) => e.target.style.transform = 'scale(1.1)'}
                    onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                  >
                    ✎
                  </label>

                  {showPhotoMenu && (
                    <>
                      {/* Overlay to close menu when clicking outside */}
                      <div 
                        style={{
                          position: 'fixed',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          zIndex: 999
                        }}
                        onClick={() => setShowPhotoMenu(false)}
                      />
                      
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        right: '-10px',
                        background: isLightTheme
                          ? '#ffffff'
                          : 'linear-gradient(135deg, rgba(3, 12, 30, 0.96), rgba(7, 22, 52, 0.96))',
                        border: isLightTheme
                          ? '1px solid rgba(13, 35, 72, 0.1)'
                          : '1px solid rgba(78, 148, 255, 0.2)',
                        borderRadius: '10px',
                        marginTop: '8px',
                        minWidth: '170px',
                        boxShadow: isLightTheme
                          ? '0 16px 26px rgba(9, 20, 45, 0.15)'
                          : '0 18px 38px rgba(0, 5, 18, 0.65)',
                        overflow: 'hidden',
                        zIndex: 2001,
                        animation: 'slideDown 0.2s ease-out'
                      }}>
                      <button
                        onClick={() => {
                          document.getElementById('profile-photo-input').click();
                          setShowPhotoMenu(false);
                        }}
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          border: 'none',
                          background: 'transparent',
                          color: navyTextPrimary,
                          textAlign: 'left',
                          cursor: 'pointer',
                          fontSize: '13px',
                          transition: 'all 0.2s ease',
                          borderBottom: isLightTheme
                            ? '1px solid rgba(13, 35, 72, 0.08)'
                            : '1px solid rgba(255,255,255,0.08)'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = isLightTheme
                            ? 'rgba(14, 44, 92, 0.08)'
                            : 'rgba(77, 168, 255, 0.1)';
                          e.target.style.paddingLeft = '20px';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = 'transparent';
                          e.target.style.paddingLeft = '16px';
                        }}
                      >
                        📤 Upload Photo
                      </button>
                      {profileImage && (
                        <button
                          onClick={handleProfileImageDelete}
                          style={{
                            width: '100%',
                            padding: '12px 16px',
                            border: 'none',
                            background: 'transparent',
                            color: '#ff6b6b',
                            textAlign: 'left',
                            cursor: 'pointer',
                            fontSize: '13px',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = 'rgba(255, 107, 107, 0.15)';
                            e.target.style.paddingLeft = '20px';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = 'transparent';
                            e.target.style.paddingLeft = '16px';
                          }}
                        >
                          🗑️ Delete Photo
                        </button>
                      )}
                    </div>
                    </>
                  )}
                  <input
                    id="profile-photo-input"
                    type="file"
                    accept="image/*"
                    onChange={handleProfileImageChange}
                    style={{ display: 'none' }}
                  />
                </div>
              </div>

              {/* User Information Section */}
              <div
                style={{
                  ...profileSurfaceStyle,
                  width: '100%',
                  padding: '24px'
                }}
              >
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '16px'
                  }}>
                    <div
                      style={{ ...profileInfoCardStyle }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = isLightTheme
                          ? '0 16px 30px rgba(13, 35, 78, 0.18)'
                          : '0 18px 34px rgba(0, 5, 20, 0.55)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      <label style={{
                        fontSize: '11px',
                        color: infoLabelColor,
                        display: 'block',
                        marginBottom: '8px',
                        textTransform: 'uppercase',
                        letterSpacing: '1px',
                        fontWeight: '700'
                      }}>👤 Name</label>
                      <div style={{
                        fontSize: '16px',
                        color: navyTextPrimary,
                        fontWeight: '700'
                      }}>{userInfo.username || 'Not set'}</div>
                    </div>
                    
                    <div
                      style={{ ...profileInfoCardStyle }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = isLightTheme
                          ? '0 16px 30px rgba(13, 35, 78, 0.18)'
                          : '0 18px 34px rgba(0, 5, 20, 0.55)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      <label style={{
                        fontSize: '11px',
                        color: infoLabelColor,
                        display: 'block',
                        marginBottom: '6px',
                        textTransform: 'uppercase',
                        letterSpacing: '1px',
                        fontWeight: '700'
                      }}>📧 Email</label>
                      <div style={{
                        fontSize: '16px',
                        color: navyTextPrimary,
                        fontWeight: '700',
                        wordBreak: 'break-word'
                      }}>{userInfo.email || 'Not set'}</div>
                    </div>
                    
                    <div
                      style={{ ...profileInfoCardStyle }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = isLightTheme
                          ? '0 16px 30px rgba(13, 35, 78, 0.18)'
                          : '0 18px 34px rgba(0, 5, 20, 0.55)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      <label style={{
                        fontSize: '11px',
                        color: infoLabelColor,
                        display: 'block',
                        marginBottom: '6px',
                        textTransform: 'uppercase',
                        letterSpacing: '1px',
                        fontWeight: '700'
                      }}>📅 Joined</label>
                      <div style={{
                        fontSize: '16px',
                        color: navyTextPrimary,
                        fontWeight: '700'
                      }}>
                        {userInfo.created_at 
                          ? new Date(userInfo.created_at).toLocaleDateString('en-US', { 
                              year: 'numeric', 
                              month: 'short', 
                              day: 'numeric' 
                            })
                          : 'Not available'}
                      </div>
                    </div>
                  </div>
                </div>

              {/* Achievements Section */}
              <div
                style={{
                  ...profileSurfaceStyle,
                  width: '100%',
                  padding: '28px'
                }}
              >
                <h3 style={{ 
                  marginBottom: '6px',
                  fontSize: '20px',
                  fontWeight: '600',
                  color: navyTextPrimary
                }}>Achievements & Badges</h3>
                <p style={{ 
                  color: navyMutedText,
                  fontSize: '13px',
                  marginBottom: '24px'
                }}>Unlock badges by completing tasks</p>

                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '20px'
                }}>
                    <div style={{
                      background: isBeginnerUnlocked 
                        ? (isLightTheme 
                            ? 'linear-gradient(135deg, rgba(84, 159, 255, 0.25), rgba(188, 226, 255, 0.85))' 
                            : 'linear-gradient(135deg, rgba(17, 73, 122, 0.95), rgba(46, 137, 255, 0.85))') 
                        : profileBadgeLockedBg,
                      borderRadius: '18px',
                      padding: '28px 24px',
                      textAlign: 'center',
                      border: isBeginnerUnlocked 
                        ? (isLightTheme 
                            ? '1px solid rgba(64, 126, 214, 0.35)' 
                            : '1px solid rgba(95, 168, 255, 0.45)') 
                        : '1px solid rgba(255, 255, 255, 0.08)',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer',
                      opacity: isBeginnerUnlocked ? 1 : 0.55,
                      transform: isBeginnerUnlocked ? 'translateY(-2px)' : 'none',
                      boxShadow: isBeginnerUnlocked 
                        ? (isLightTheme 
                            ? '0 18px 32px rgba(10, 40, 90, 0.18)' 
                            : profileBadgeShadow)
                        : '0 10px 22px rgba(0,0,0,0.35)',
                      color: isBeginnerUnlocked 
                        ? (isLightTheme ? '#0b1f3f' : '#ffffff') 
                        : navyTextPrimary
                    }}>
                      <div style={{ fontSize: '48px', marginBottom: '12px' }}>
                        <svg width="56" height="56" viewBox="0 0 40 40" style={{ filter: isBeginnerUnlocked ? 'none' : 'grayscale(100%)' }}>
                          <circle cx="20" cy="20" r="18" fill="#5bc0f8" opacity="0.18"/>
                          <path d="M20 5 L25 15 L35 16 L27 23 L30 33 L20 28 L10 33 L13 23 L5 16 L15 15 Z" fill="#5bc0f8" />
                        </svg>
                      </div>
                      <div style={{
                        fontSize: '16px',
                        fontWeight: '700',
                        marginBottom: '6px'
                      }}>Beginner</div>
                      <div style={{
                        fontSize: '12px',
                        color: isBeginnerUnlocked ? 'rgba(255,255,255,0.8)' : navyMutedText
                      }}>Complete 1 task</div>
                    </div>

                    <div style={{
                      background: isMasterUnlocked 
                        ? (isLightTheme
                            ? 'linear-gradient(135deg, rgba(255, 213, 112, 0.2), rgba(255, 241, 199, 0.85))'
                            : 'linear-gradient(135deg, rgba(18, 52, 96, 0.96), rgba(255, 196, 0, 0.88))') 
                        : profileBadgeLockedBg,
                      borderRadius: '18px',
                      padding: '28px 24px',
                      textAlign: 'center',
                      border: isMasterUnlocked 
                        ? (isLightTheme
                            ? '1px solid rgba(255, 196, 0, 0.35)'
                            : '1px solid rgba(255, 210, 64, 0.5)') 
                        : '1px solid rgba(255, 255, 255, 0.08)',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer',
                      opacity: isMasterUnlocked ? 1 : 0.55,
                      transform: isMasterUnlocked ? 'translateY(-2px)' : 'none',
                      boxShadow: isMasterUnlocked 
                        ? (isLightTheme 
                            ? '0 18px 32px rgba(145, 115, 10, 0.22)' 
                            : profileBadgeShadow)
                        : '0 10px 22px rgba(0,0,0,0.35)',
                      color: isMasterUnlocked 
                        ? (isLightTheme ? '#5b4200' : '#ffffff') 
                        : navyTextPrimary
                    }}>
                      <div style={{ fontSize: '48px', marginBottom: '12px' }}>
                        <svg width="56" height="56" viewBox="0 0 40 40" style={{ filter: isMasterUnlocked ? 'none' : 'grayscale(100%)' }}>
                          <circle cx="20" cy="20" r="18" fill="#ffd85a" opacity="0.18"/>
                          <rect x="12" y="22" width="16" height="12" rx="2" fill="#ffd85a" />
                          <path d="M14 22 L14 18 L16 12 L20 10 L24 12 L26 18 L26 22" fill="#ffd85a" />
                          <circle cx="20" cy="16" r="3" fill="#fff" />
                        </svg>
                      </div>
                      <div style={{
                        fontSize: '16px',
                        fontWeight: '700',
                        marginBottom: '6px'
                      }}>Task Master</div>
                      <div style={{
                        fontSize: '12px',
                        color: isMasterUnlocked ? 'rgba(255,255,255,0.8)' : navyMutedText
                      }}>{completedTasks.length}/10 tasks</div>
                    </div>

                    <div style={{
                      background: isProductiveUnlocked 
                        ? (isLightTheme
                            ? 'linear-gradient(135deg, rgba(104, 93, 255, 0.18), rgba(217, 208, 255, 0.85))'
                            : 'linear-gradient(135deg, rgba(26, 47, 96, 0.96), rgba(108, 76, 255, 0.9))') 
                        : profileBadgeLockedBg,
                      borderRadius: '18px',
                      padding: '28px 24px',
                      textAlign: 'center',
                      border: isProductiveUnlocked 
                        ? (isLightTheme
                            ? '1px solid rgba(108, 76, 255, 0.35)'
                            : '1px solid rgba(139, 104, 255, 0.45)') 
                        : '1px solid rgba(255, 255, 255, 0.08)',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer',
                      opacity: isProductiveUnlocked ? 1 : 0.55,
                      transform: isProductiveUnlocked ? 'translateY(-2px)' : 'none',
                      boxShadow: isProductiveUnlocked 
                        ? (isLightTheme 
                            ? '0 18px 32px rgba(55, 31, 128, 0.18)'
                            : profileBadgeShadow)
                        : '0 10px 22px rgba(0,0,0,0.35)',
                      color: isProductiveUnlocked 
                        ? (isLightTheme ? '#27145a' : '#ffffff') 
                        : navyTextPrimary
                    }}>
                      <div style={{ fontSize: '48px', marginBottom: '12px' }}>
                        <svg width="56" height="56" viewBox="0 0 40 40" style={{ filter: isProductiveUnlocked ? 'none' : 'grayscale(100%)' }}>
                          <circle cx="20" cy="20" r="18" fill="#c39bff" opacity="0.18"/>
                          <polygon points="20,8 24,16 33,17 26,24 28,33 20,28 12,33 14,24 7,17 16,16" fill="#c39bff" />
                          <circle cx="20" cy="20" r="5" fill="#fff" />
                        </svg>
                      </div>
                      <div style={{
                        fontSize: '16px',
                        fontWeight: '700',
                        marginBottom: '6px'
                      }}>Productive</div>
                      <div style={{
                        fontSize: '12px',
                        color: isProductiveUnlocked ? 'rgba(255,255,255,0.8)' : navyMutedText
                      }}>{completedTasks.length}/50 tasks</div>
                    </div>

                    <div style={{
                      background: isLegendUnlocked 
                        ? (isLightTheme
                            ? 'linear-gradient(135deg, rgba(255, 171, 209, 0.2), rgba(255, 227, 239, 0.9))'
                            : 'linear-gradient(135deg, rgba(23, 38, 92, 0.96), rgba(255, 105, 180, 0.88))') 
                        : profileBadgeLockedBg,
                      borderRadius: '18px',
                      padding: '28px 24px',
                      textAlign: 'center',
                      border: isLegendUnlocked 
                        ? (isLightTheme
                            ? '1px solid rgba(255, 133, 195, 0.35)'
                            : '1px solid rgba(255, 133, 195, 0.45)') 
                        : '1px solid rgba(255, 255, 255, 0.08)',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer',
                      opacity: isLegendUnlocked ? 1 : 0.55,
                      transform: isLegendUnlocked ? 'translateY(-2px)' : 'none',
                      boxShadow: isLegendUnlocked 
                        ? (isLightTheme 
                            ? '0 18px 32px rgba(146, 41, 92, 0.2)'
                            : profileBadgeShadow)
                        : '0 10px 22px rgba(0,0,0,0.35)',
                      color: isLegendUnlocked 
                        ? (isLightTheme ? '#5b123b' : '#ffffff') 
                        : navyTextPrimary
                    }}>
                      <div style={{ fontSize: '48px', marginBottom: '12px' }}>
                        <svg width="56" height="56" viewBox="0 0 40 40" style={{ filter: isLegendUnlocked ? 'none' : 'grayscale(100%)' }}>
                          <circle cx="20" cy="20" r="18" fill="#ff9ad5" opacity="0.18"/>
                          <path d="M20 5 L23 12 L30 13 L25 18 L26 25 L20 21 L14 25 L15 18 L10 13 L17 12 Z" fill="#ff9ad5" />
                          <path d="M20 8 L22 13 L27 14 L23 17 L24 22 L20 19 L16 22 L17 17 L13 14 L18 13 Z" fill="#ffe082" />
                        </svg>
                      </div>
                      <div style={{
                        fontSize: '16px',
                        fontWeight: '700',
                        marginBottom: '6px'
                      }}>Legend</div>
                      <div style={{
                        fontSize: '12px',
                        color: isLegendUnlocked ? 'rgba(255,255,255,0.8)' : navyMutedText
                      }}>{completedTasks.length}/100 tasks</div>
                    </div>
                  </div>

                  <div style={{ marginTop: '24px' }}>
                    <div style={{
                      fontSize: '12px',
                      color: navyMutedText,
                      marginBottom: '8px',
                      display: 'flex',
                      justifyContent: 'space-between'
                    }}>
                      <span>Overall Progress</span>
                      <span>{profileProgressPercent}%</span>
                    </div>
                    <div style={{
                      height: '9px',
                      background: profileProgressTrack,
                      borderRadius: '999px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        height: '100%',
                        width: `${profileProgressPercent}%`,
                        background: 'linear-gradient(90deg, #0f3f92, #007adf, #5fd4ff)',
                        borderRadius: '999px',
                        transition: 'width 0.5s ease'
                      }}/>
                    </div>
                  </div>
                </div>

                {/* Account Stats Section */}
                <div
                  style={{
                    ...profileSurfaceStyle,
                    width: '100%',
                    padding: '28px'
                  }}
                >
                  <div style={{ marginBottom: '24px', width: '100%' }}>
                    <h3 style={{ 
                      marginBottom: '6px',
                      fontSize: '20px',
                      fontWeight: '600',
                      color: navyTextPrimary
                    }}>Account Details</h3>
                    <p style={{ 
                      color: navyMutedText, 
                      fontSize: '13px',
                      margin: 0
                    }}>Your account details and preferences</p>
                  </div>

                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '20px'
                  }}>
                    <div style={{
                      padding: '18px',
                      borderRadius: '16px',
                      background: profileAccountCardBg,
                      border: profileAccountBorder
                    }}>
                      <div style={{
                        fontSize: '12px',
                        color: navyMutedText,
                        marginBottom: '8px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.6px',
                        fontWeight: '600'
                      }}>Account Age</div>
                      <div style={{
                        fontSize: '16px',
                        color: navyTextPrimary,
                        fontWeight: '600'
                      }}>
                        {userInfo.created_at 
                          ? Math.floor((new Date() - new Date(userInfo.created_at)) / (1000 * 60 * 60 * 24)) + ' days'
                          : 'Recently created'}
                      </div>
                    </div>

                    <div style={{
                      padding: '18px',
                      borderRadius: '16px',
                      background: profileAccountCardBg,
                      border: profileAccountBorder
                    }}>
                      <div style={{
                        fontSize: '12px',
                        color: navyMutedText,
                        marginBottom: '8px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.6px',
                        fontWeight: '600'
                      }}>Account Status</div>
                      <div style={{
                        fontSize: '16px',
                        color: '#4fe0ff',
                        fontWeight: '600'
                      }}>Active & Verified</div>
                    </div>

                    <div style={{
                      padding: '18px',
                      borderRadius: '16px',
                      background: profileAccountCardBg,
                      border: profileAccountBorder
                    }}>
                      <div style={{
                        fontSize: '12px',
                        color: navyMutedText,
                        marginBottom: '8px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.6px',
                        fontWeight: '600'
                      }}>Theme Preference</div>
                      <div style={{
                        fontSize: '16px',
                        color: navyTextPrimary,
                        fontWeight: '600'
                      }}>
                        {isLightTheme ? '☀️ Light' : '🌙 Dark'}
                      </div>
                    </div>

                    <div style={{
                      padding: '18px',
                      borderRadius: '16px',
                      background: profileAccountCardBg,
                      border: profileAccountBorder
                    }}>
                      <div style={{
                        fontSize: '12px',
                        color: navyMutedText,
                        marginBottom: '8px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.6px',
                        fontWeight: '600'
                      }}>Profile Completion</div>
                      <div style={{
                        fontSize: '16px',
                        color: '#5ea8ff',
                        fontWeight: '600'
                      }}>{profileProgressPercent}%</div>
                    </div>
                  </div>
                </div>
            </div>
            </>
          ) : activeTab === "settings" ? (
            <Settings />
          ) : activeTab === "calendar" ? (
            <CalendarView
              pendingTasks={pendingTasks}
              missedTasks={missedTasks}
              completedTasks={completedTasks}
              onNavigateToTasks={handleCalendarNavigate}
            />
          ) : activeTab === "dashboard" ? (
            <>
              <DashboardOverview
                timeString={timeString}
                dateString={dateString}
                pendingTasks={pendingTasks}
                missedTasks={missedTasks}
                completedTasks={completedTasks}
                graphFilter={graphFilter}
                setGraphFilter={setGraphFilter}
                getFilteredData={getFilteredData}
                completionRate={completionRate}
                totalTasks={totalTasks}
                nextDueTask={nextDueTask}
                oldestOverdue={oldestOverdue}
                handleOpenTasks={() => handleSetActiveTab("tasks")}
              />

              <DashboardFooter
                pendingTasks={pendingTasks}
                setIsSupportOpen={setIsSupportOpen}
                setIsPrivacyOpen={setIsPrivacyOpen}
              />
            </>
          ) : (
            <TasksView
              filter={filter}
              setFilter={setFilter}
              selectedFilter={selectedFilter}
              setSelectedFilter={setSelectedFilter}
              applyFilter={applyFilter}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              pendingTasks={pendingTasks}
              missedTasks={missedTasks}
              completedTasks={completedTasks}
              setIsModalOpen={setIsModalOpen}
              resetNewTaskForm={resetNewTaskForm}
              setSelectedTask={setSelectedTask}
              setIsViewModalOpen={setIsViewModalOpen}
              setIsEditMode={setIsEditMode}
              handleUpdateStatus={handleUpdateStatus}
              handleDelete={handleDelete}
              deletingTaskId={deletingTaskId}
              updatingTaskId={updatingTaskId}
              setNewTask={setNewTask}
              handleSave={handleAddTask}
            />
          )}
        </div>
      </main>

      {/* All Modals */}
      <Modals
        isPrivacyOpen={isPrivacyOpen}
        setIsPrivacyOpen={setIsPrivacyOpen}
        isLogoutConfirmOpen={isLogoutConfirmOpen}
        setIsLogoutConfirmOpen={setIsLogoutConfirmOpen}
        handleLogout={handleLogout}
        isViewModalOpen={isViewModalOpen}
        setIsViewModalOpen={setIsViewModalOpen}
        selectedTask={selectedTask}
        setSelectedTask={setSelectedTask}
        isEditMode={isEditMode}
        setIsEditMode={setIsEditMode}
        handleSaveEdit={handleSaveEdit}
        isSupportOpen={isSupportOpen}
        setIsSupportOpen={setIsSupportOpen}
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        newTask={newTask}
        setNewTask={setNewTask}
        handleAddTask={handleAddTask}
        handleAIMagic={handleAIMagic}
        isAiLoading={isAiLoading}
        isSavingTask={isSavingTask}
        isEditingSaving={isEditingSaving}
      />
    </div>
  );
};

export default Dashboard;